// These are types used by the charts on the dashboard page.

export const BAR_CHARTS = [
    'Embodied Carbon Intensity by Element',
    'Embodied Carbon Intensity by Material',
    'Concrete Use Intensity by Element',
    'Steel Use Intensity by Element',
    'Timber Use Intensity by Element'
] as const;
export const PERFORMANCE_OVER_TIME_PLOTS = [ 'Performance Over Time' ] as const;
export const MATERIAL_BOX_PLOTS = [
    'Concrete Use Intensity - All Projects',
    'Steel Use Intensity - All Projects',
    'Timber Use Intensity - All Projects'
];
export const BOX_PLOTS = [
    'Primary Building Use Type',
    'Primary Horizontal Gravity System',
    'Primary Lateral System',
    'Number of Stories Above Grade',
    'Gross Floor Area',
    ...PERFORMANCE_OVER_TIME_PLOTS,
    ...MATERIAL_BOX_PLOTS
] as const;
export const HEAT_MAPS = [ 'Project Heat Map' ] as const;

export const ASSESSMENT_STAGES = [ 'A1-A5', 'A1-A3', 'A1-A4', 'A-C' ] as const;
export const DATA_POINT_TYPES = [ 'GWP', 'Concrete', 'Steel', 'Timber' ] as const;
export const INTENSITY_METRICS = [ 'Use', 'Carbon' ] as const;
export const PROJECT_LISTS = [ 'All Projects', 'My Firm Projects' ] as const;
export const FIT_TO_OPTIONS = [ 'Zoomed In to Core Distribution', 'Zoomed Out to Show Outliers' ] as const;

export type BarChartType = (typeof BAR_CHARTS)[number];
export function isBarChartType(value: any): value is BarChartType {
    return BAR_CHARTS.includes(value);
}

export type BoxPlotType = (typeof BOX_PLOTS)[number];
export function isBoxPlotType(value: any): value is BoxPlotType {
    return BOX_PLOTS.includes(value);
}

export type PerformanceOverTimeType = (typeof PERFORMANCE_OVER_TIME_PLOTS)[number];
export function isPerformanceOverTimeType(value: any): value is PerformanceOverTimeType {
    return PERFORMANCE_OVER_TIME_PLOTS.includes(value);
}

export type MaterialBoxPlotType = (typeof MATERIAL_BOX_PLOTS)[number];
export function isMaterialBoxPlotType(value: any): value is MaterialBoxPlotType {
    return MATERIAL_BOX_PLOTS.includes(value);
}

export type HeatMapType = (typeof HEAT_MAPS)[number];
export function isHeatMapType(value: any): value is HeatMapType {
    return HEAT_MAPS.includes(value);
}

export type ChartType = BarChartType | BoxPlotType | HeatMapType;
export function isChartType(value: any): value is ChartType {
    return isBarChartType(value) || isBoxPlotType(value) || isHeatMapType(value);
}

export type AssessmentStageType = (typeof ASSESSMENT_STAGES)[number];
export function isAssessmentStageType(value: any): value is AssessmentStageType {
    return ASSESSMENT_STAGES.includes(value);
}

export type DataPointType = (typeof DATA_POINT_TYPES)[number];
export function isDataPointType(value: any): value is DataPointType {
    return DATA_POINT_TYPES.includes(value);
}

export type IntensityMetricType = (typeof INTENSITY_METRICS)[number];
export function isIntensityMetricType(value: any): value is IntensityMetricType {
    return INTENSITY_METRICS.includes(value);
}

export type ProjectListType = (typeof PROJECT_LISTS)[number];
export function isProjectListType(value: any): value is ProjectListType {
    return PROJECT_LISTS.includes(value);
}

export type FitToOptionType = (typeof FIT_TO_OPTIONS)[number];
export function isFitToOptionType(value: any): value is FitToOptionType {
    return FIT_TO_OPTIONS.includes(value);
}

export type FirmOptionType = {
    text: string;
    value: string;
};

export type TypeGroup = {
    category: string;
    subtypes: string[];
};

export interface RawBreakdownItem {
  name: string;
  firm: string | null;
  data: any[];
}

export interface RawBoxwhiskersItem {
  firm: string | null;
  is_firm_project: boolean;
  subnational: string;
  country: string;
  submitted_at: string;
  primary_building_use_type: string;
  construction_type: string;
  primary_vertical_gravity_system: string;
  primary_horizontal_gravity_system: string;
  primary_lateral_system: string;
  gross_area_sqft: number;
  gross_area_sqm: number;
  mean_roof_height_ft: number;
  stories_above_grade: number;
  stories_below_grade: number;
  a1_a3_gwp_kg: number;
  a4_gwp_kg: number | null;
  a5_gwp_kg: number | null;
  b1_b5_gwp_kg: number | null;
  c1_c4_gwp_kg: number | null;
  d_gwp_kg: number | null;
  biogenic_carbon_flow_kg: number;
  quantity: number | null;
  material_unit: string | null;
}

